"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useMemo } from "react";
import { ArrowRight, BookOpen, Calendar, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { PostsProvider } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useCategoryPosts } from "@/hooks/useCategoryPosts";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { decodeHtmlEntities } from "@/lib/utils";
import type { Lang } from "@/lib/i18n";
interface FallbackArticle {
  id: string;
  title: Record<Lang, string>;
  excerpt: Record<Lang, string>;
  category: Record<Lang, string>;
  link: string;
  imgSrc: string;
  imgSrcset: string;
  imgSizes: string;
}
const FALLBACK_ARTICLES: FallbackArticle[] = [{
  id: "fb-1",
  title: {
    en: "Understanding Wire Mesh Aperture & Micron Ratings in High-Pressure Filtration",
    zh: "理解高压过滤中的丝网孔径与微米过滤精度等级",
    es: "Comprensión de apertura y clasificación en micras de malla en filtración a alta presión",
    de: "Maschenweite und Mikron-Filterfeinheit bei Hochdruckfiltration verstehen",
    fr: "Comprendre l'ouverture de maille et le seuil de filtration sous haute pression",
    pt: "Entendendo abertura de malha e classificação em mícrons na filtração sob alta pressão"
  },
  excerpt: {
    en: "An engineering breakdown of standard mesh counts, wire diameters, and effective pore size calculation methods for industrial liquid separation.",
    zh: "针对工业液体分离的标准目数、丝径以及有效孔径计算方法的工程解析。",
    es: "Análisis de ingeniería de números de malla estándar, diámetros de alambre y métodos de cálculo de poro efectivo.",
    de: "Ingenieurtechnische Analyse von Standard-Maschenzahlen, Drahtdurchmessern und effektiven Porengrößen.",
    fr: "Analyse technique des comptes de maille standard, diamètres de fil et méthodes de calcul des pores.",
    pt: "Análise técnica de contagens de malha padrão, diâmetros de arame e métodos de cálculo de poro efetivo."
  },
  category: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  link: "/WireMeshApertureMeasurementPage",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_macro.webp",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_macro.webp-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/woven_wire_mesh_macro.webp-wvc-sizes"
}, {
  id: "fb-2",
  title: {
    en: "Selecting Stainless Steel Grades: 304 and 316L for Corrosive Environments",
    zh: "不锈钢牌号选择：304 与 316L 在腐蚀环境中的应用",
    es: "Selección de grados de acero inoxidable: 304 y 316L para ambientes corrosivos",
    de: "Edelstahlgüten im Vergleich: 304 und 316L für korrosive Umgebungen",
    fr: "Choix des nuances d'acier inoxydable : 304 et 316L en milieu corrosif",
    pt: "Seleção de graus de aço inoxidável: 304 e 316L para ambientes corrosivos"
  },
  excerpt: {
    en: "Evaluate mechanical properties, pitting resistance, and thermal tolerance across critical chemical and polymer filtration processes.",
    zh: "评估关键化学与聚合物过滤工艺中的机械性能、耐点蚀能力以及耐热耐受极限。",
    es: "Evaluación de propiedades mecánicas, resistencia a picaduras y tolerancia térmica en procesos químicos y de polímeros.",
    de: "Bewertung mechanischer Eigenschaften, Lochfraßbeständigkeit und thermischer Toleranz in Chemie- und Polymerprozessen.",
    fr: "Évaluation des propriétés mécaniques, de la résistance aux piqûres et de la tolérance thermique en chimie et polymères.",
    pt: "Avaliação de propriedades mecânicas, resistência à corrosão por pite e tolerância térmica em química e polímeros."
  },
  category: {
    en: "Materials Science",
    zh: "材料科学",
    es: "Ciencia de materiales",
    de: "Werkstoffkunde",
    fr: "Science des matériaux",
    pt: "Ciência dos materiais"
  },
  link: "/StainlessSteelGradeSelectionPage",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_mesh_structure.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_mesh_structure.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_mesh_structure.png-wvc-sizes"
}, {
  id: "fb-3",
  title: {
    en: "Sintered vs. Woven Wire Mesh: Structural Integrity & Cleanability Comparison",
    zh: "烧结网与编织网对比：结构完整性与可清洗性分析",
    es: "Malla sinterizada frente a tejida: comparación de integridad estructural y facilidad de limpieza",
    de: "Sinter- vs. Drahtgewebe: Vergleich von Strukturfestigkeit und Reinigungsfähigkeit",
    fr: "Toile frittée ou tissée : comparaison de l'intégrité structurelle et de la nettoyabilité",
    pt: "Malha sinterizada vs. tecida: comparação de integridade estrutural e facilidade de limpeza"
  },
  excerpt: {
    en: "Discover how diffusion bonding multi-layer mesh panels prevents wire migration and extends element life cycles in continuous filtration processes.",
    zh: "了解多层丝网扩散焊接如何防止金属丝位移，并延长连续过滤工艺中的滤芯使用周期。",
    es: "Cómo la unión por difusión en paneles multicapa evita el desplazamiento de alambres y prolonga la vida útil.",
    de: "Wie Diffusionsschweißen mehrlagiger Gewebelaminate Drahtverzug verhindert und Standzeiten verlängert.",
    fr: "Comment le soudage par diffusion de panneaux multicouches prévient la migration des fils et allonge la durée de vie.",
    pt: "Como a união por difusão em painéis multicamadas evita o deslocamento de fios e estende a vida útil."
  },
  category: {
    en: "Application Engineering",
    zh: "应用工程",
    es: "Ingeniería de aplicaciones",
    de: "Anwendungstechnik",
    fr: "Ingénierie applicative",
    pt: "Engenharia de aplicação"
  },
  link: "/SinteredWireMeshSelectionGuidePage",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_filtration_vessel.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_filtration_vessel.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial_filtration_vessel.png-wvc-sizes"
}];
function StripHtml({
  html
}: {
  html?: string;
}) {
  if (!html) return null;
  const cleanText = html.replace(/<[^>]*>?/gm, "").trim();
  return <>{decodeHtmlEntities(cleanText)}</>;
}
function ArticlesConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    ready
  } = useCategoryPosts({
    categoryIds: [203],
    pageSize: 3
  });
  const copy = useMemo(() => ({
    errorText: pick(lang, {
      en: "Unable to retrieve technical publications. Please check your connection and retry.",
      zh: "无法加载技术文献，请检查网络后重试。",
      es: "No se pudieron cargar las publicaciones técnicas. Compruebe su conexión y reintente.",
      de: "Technische Publikationen konnten nicht geladen werden. Bitte Verbindung prüfen.",
      fr: "Impossible de charger les publications techniques. Veuillez vérifier votre connexion.",
      pt: "Não foi possível carregar as publicações técnicas. Verifique sua conexão e tente novamente."
    }),
    retry: pick(lang, {
      en: "Retry Connection",
      zh: "重试连接",
      es: "Reintentar conexión",
      de: "Verbindung wiederholen",
      fr: "Réessayer la connexion",
      pt: "Reconectar"
    }),
    readArticle: pick(lang, {
      en: "Read Technical Guide",
      zh: "阅读技术指南",
      es: "Leer guía técnica",
      de: "Leitfaden lesen",
      fr: "Lire le guide technique",
      pt: "Ler guia técnico"
    }),
    categoryDefault: pick(lang, {
      en: "Technical Reference",
      zh: "技术文献",
      es: "Referencia técnica",
      de: "Technische Referenz",
      fr: "Référence technique",
      pt: "Referência técnica"
    })
  }), [lang]);
  if (loading || !ready) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex flex-col justify-between rounded-lg border border-border bg-card p-6 min-h-[440px]">
            <div className="space-y-4">
              <Skeleton className="mf-card-media mf-ratio-card w-full" />
              <Skeleton className="h-4 w-1/3 rounded" />
              <Skeleton className="h-6 w-5/6 rounded" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-4/5 rounded" />
              </div>
            </div>
            <Skeleton className="h-5 w-28 rounded mt-6" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="p-8 text-center rounded-lg border border-border bg-card">
        <p className="text-muted-foreground text-sm mb-4">
          {copy.errorText}
        </p>
        <Button variant="outline" className="h-11 min-h-[44px]" onClick={() => refetch()}>
          <RefreshCw className="size-4 mr-2" aria-hidden="true" />
          {copy.retry}
        </Button>
      </div>;
  }
  const displayPosts = posts && posts.length > 0 ? posts.slice(0, 3) : null;
  if (!displayPosts) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {FALLBACK_ARTICLES.map((article, i) => <Reveal key={article.id} delay={Math.min(i * 60, 300)}>
            <article data-index={i} className="group flex flex-col justify-between h-full rounded-lg border border-border bg-card text-card-foreground hover:border-primary/40 transition-colors overflow-hidden mf-hover-lift">
              <div>
                <div className="relative mf-card-media mf-ratio-card overflow-hidden">
                  <img src={article.imgSrc} data-wvc-srcset={article.imgSrcset} data-wvc-sizes={article.imgSizes} alt={pick(lang, article.title)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="outline" className="bg-card border border-border text-foreground mf-caption font-mono">
                      {pick(lang, article.category)}
                    </Badge>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="mf-h3 text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                    {pick(lang, article.title)}
                  </h3>

                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {pick(lang, article.excerpt)}
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-0 mt-auto">
                <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-sm text-primary hover:bg-transparent hover:text-primary/80 font-medium group/btn min-h-[44px] justify-start">
                  <Link to={article.link}>
                    <span>{copy.readArticle}</span>
                    <ChevronRight className="size-4 ml-1" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </article>
          </Reveal>)}
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {displayPosts.map((post, i) => {
      const fallback = FALLBACK_ARTICLES[i % FALLBACK_ARTICLES.length];
      const postLink = post.link || "/TechnicalResourcesPage";
      const formattedDate = post.date ? new Date(post.date).toLocaleDateString(lang === "zh" ? "zh-CN" : lang === "de" ? "de-DE" : lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) : null;
      const categoryName = post.categories && post.categories.length > 0 ? decodeHtmlEntities(post.categories[0].name || "") : copy.categoryDefault;
      return <Reveal key={post.id} delay={Math.min(i * 60, 300)}>
            <article data-index={i} className="group flex flex-col justify-between h-full rounded-lg border border-border bg-card text-card-foreground hover:border-primary/40 transition-colors overflow-hidden mf-hover-lift">
              <div>
                <div className="relative mf-card-media mf-ratio-card overflow-hidden">
                  {post.featuredImage ? <img src={post.featuredImage} alt={decodeHtmlEntities(post.title)} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <img src={fallback.imgSrc} data-wvc-srcset={fallback.imgSrcset} data-wvc-sizes={fallback.imgSizes} alt={decodeHtmlEntities(post.title)} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                  <div className="absolute top-3 left-3">
                    <Badge variant="outline" className="bg-card border border-border text-foreground mf-caption font-mono">
                      {categoryName}
                    </Badge>
                  </div>
                </div>

                <div className="p-6">
                  {formattedDate && <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <SafeHydrate><Calendar className="size-4 shrink-0" aria-hidden="true" /></SafeHydrate>
                      <span>{formattedDate}</span>
                    </div>}

                  <h3 className="mf-h3 text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                    {decodeHtmlEntities(post.title)}
                  </h3>

                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    <StripHtml html={post.excerpt || post.content} />
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-0 mt-auto">
                <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-sm text-primary hover:bg-transparent hover:text-primary/80 font-medium group/btn min-h-[44px] justify-start">
                  <Link to={postLink}>
                    <span>{copy.readArticle}</span>
                    <ChevronRight className="size-4 ml-1" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </article>
          </Reveal>;
    })}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  const wpQuery = useMemo(() => {
    return new WP_Query({
      post_type: "post",
      per_page: 30,
      order: "desc",
      orderby: "date",
      status: ["publish"],
      categories: [203]
    });
  }, []);
  const copy = useMemo(() => ({
    eyebrow: pick(lang, {
      en: "Engineering & Application Knowledge",
      zh: "工程与应用知识",
      es: "Conocimiento técnico y de aplicaciones",
      de: "Engineering- & Anwendungswissen",
      fr: "Connaissances d'ingénierie et d'application",
      pt: "Conhecimento técnico e de aplicações"
    }),
    heading: pick(lang, {
      en: "Related technical articles & guides",
      zh: "相关技术文章与指南",
      es: "Artículos técnicos y guías relacionadas",
      de: "Verwandte Fachartikel & Leitfäden",
      fr: "Articles techniques et guides associés",
      pt: "Artigos técnicos e guias relacionados"
    }),
    lead: pick(lang, {
      en: "In-depth technical analysis, mesh selection frameworks, and material compatibility standards curated for B2B industrial engineering teams.",
      zh: "为工业工程与采购团队整理的深度技术分析、丝网选型框架及材料耐腐蚀兼容性标准。",
      es: "Análisis técnico profundo, marcos de selección de mallas y compatibilidad de materiales para equipos de ingeniería B2B.",
      de: "Tiefgehende technische Analysen, Gewebeauswahl-Richtlinien und chemische Beständigkeitsstandards für Industrie- und Einkaufsteams.",
      fr: "Analyses techniques approfondies, critères de sélection de toiles et normes de compatibilité des matériaux pour l'ingénierie industrielle.",
      pt: "Análises técnicas aprofundadas, metodologias de seleção de telas e padrões de compatibilidade química para equipes de engenharia B2B."
    }),
    allResources: pick(lang, {
      en: "View all technical resources",
      zh: "查看全部技术资源",
      es: "Ver todos los recursos técnicos",
      de: "Alle technischen Ressourcen ansehen",
      fr: "Voir toutes les ressources techniques",
      pt: "Ver todos os recursos técnicos"
    })
  }), [lang]);
  return <section id="relatedarticles" data-nav="light" className="mf-section bg-background text-foreground border-t border-border">
      <div className="mf-container">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-border pb-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-primary mf-caption font-mono font-semibold uppercase tracking-wider mb-2">
              <BookOpen className="size-4 shrink-0" aria-hidden="true" />
              <span>{copy.eyebrow}</span>
            </div>
            <h2 className="mf-h2 text-foreground font-default">
              {copy.heading}
            </h2>
            <p className="mt-3 text-muted-foreground mf-lead">
              {copy.lead}
            </p>
          </div>

          <div className="shrink-0">
            <Button variant="outline" asChild className="px-5 text-sm gap-2 min-h-[44px] bg-card">
              <Link to="/TechnicalResourcesPage">
                <span>{copy.allResources}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </Reveal>

        <PostsProvider wp_query={wpQuery}>
          <ArticlesConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}